import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { useIsFutureTime } from '../../hooks/timeclock/useIsFutureTime'
import type { TimeClockStatus } from '../../hooks/timeclock/useTimeClock'
import { formatDuration, useElapsedTimer } from '../../hooks/useElapsedTimer'
import { FutureClockIn } from './FutureClockIn'

type TimeClockBannerProps = {
  status: { slug: TimeClockStatus; name: string; isActive: boolean }
  taskName?: string | null
  startTime?: Date | null
  shiftStartTime?: Date | null
}

/** Status slug → CSS modifier class (drives the circle's border/text color) */
const STATUS_COLOR_CLASS: Record<string, string> = {
  clockedIn: 'timeclock-status--active',
  onBreak: 'timeclock-status--break',
}

/**
 * Time clock banner — a simple bordered circle (matches the v1 crew app):
 * white circle with a 3px status-colored border, the status name, the elapsed
 * time, and (when active) the task name + shift total.
 */
export const TimeClockBanner = ({
  status,
  taskName,
  startTime,
  shiftStartTime,
}: TimeClockBannerProps) => {
  const { _ } = useLingui()

  const { elapsed, shiftElapsed } = useElapsedTimer(
    status.isActive,
    startTime,
    shiftStartTime,
  )

  // A clock-in for a shift that hasn't started yet shows "Clocked in as of …"
  // in place of the running shift total, until the start time passes.
  const isFutureShift = useIsFutureTime(shiftStartTime)

  const statusColorClass =
    STATUS_COLOR_CLASS[status.slug] ?? 'timeclock-status--idle'

  const statusLabels: Record<string, string> = {
    clockedIn: _(msg`Clocked In`),
    clockedOut: _(msg`Clocked Out`),
    onBreak: _(msg`On Break`),
  }

  const elapsedFormatted = formatDuration(elapsed)
  const shiftFormatted = formatDuration(shiftElapsed)

  return (
    <div className={`timeclock-banner ${statusColorClass}`}>
      <div className='timeclock-circle'>
        <span className='timeclock-status-name'>
          {statusLabels[status.slug] || status.name}
        </span>

        <span className='timeclock-elapsed'>
          {status.isActive
            ? `${elapsedFormatted.days > 0 ? `${elapsedFormatted.days}d ` : ''}${elapsedFormatted.time}`
            : _(msg`00:00`)}
        </span>

        {status.isActive && (
          <div className='timeclock-meta'>
            {taskName && (
              <span className='timeclock-task-name'>{taskName}</span>
            )}
            {shiftStartTime &&
              (isFutureShift ? (
                <FutureClockIn startTime={shiftStartTime} />
              ) : (
                <span className='timeclock-shift-total'>
                  {_(msg`Active Total`)}:{' '}
                  {shiftFormatted.days > 0 ? `${shiftFormatted.days}d ` : ''}
                  {shiftFormatted.time}
                </span>
              ))}
          </div>
        )}
      </div>
    </div>
  )
}
